import React, { useState, useEffect } from 'react';
import { Calendar as CalendarIcon, Trash2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { Label } from './ui/label';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Calendar } from './ui/calendar';
import PrimaryButton from './themed/PrimaryButton';
import { TimelineEvent } from '../types/invitation';
import { cn } from '../lib/utils';
import { createLogger } from '../lib/logger';

const log = createLogger('timeline');

interface TaskEditorProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (eventData: Omit<TimelineEvent, 'id' | 'created_at' | 'updated_at' | 'user_id'>) => Promise<void>;
  onUpdate: (eventId: string, updates: Partial<TimelineEvent>) => Promise<void>;
  onDelete?: (eventId: string) => Promise<void>;
  editingEvent?: TimelineEvent | null;
  invitationId: string;
}

const TaskEditor: React.FC<TaskEditorProps> = ({
  isOpen,
  onClose,
  onSave,
  onUpdate,
  onDelete,
  editingEvent,
  invitationId
}) => {
  const [formData, setFormData] = useState({
    title: '',
    target_date: '',
    description: '',
    category: '',
    is_completed: false,
    display_order: 0
  });
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // 當編輯事件改變時，更新表單數據
  useEffect(() => {
    if (editingEvent) {
      setFormData({
        title: editingEvent.title,
        target_date: editingEvent.target_date,
        description: editingEvent.description || '',
        category: editingEvent.category || '',
        is_completed: editingEvent.is_completed,
        display_order: editingEvent.display_order
      });
    } else {
      // 新增模式：重置表單
      setFormData({
        title: '',
        target_date: '',
        description: '',
        category: '',
        is_completed: false,
        display_order: Math.floor(Date.now() / 100000) // 使用更小的數值作為排序順序
      });
    }
    setErrors({});
  }, [editingEvent, isOpen]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = '請輸入任務標題';
    }

    if (!formData.target_date) {
      newErrors.target_date = '請選擇目標日期';
    } else {
      // 驗證日期格式
      try {
        new Date(formData.target_date);
      } catch {
        newErrors.target_date = '日期格式不正確';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSaving(true);
    try {
      if (editingEvent) {
        // 更新模式
        await onUpdate(editingEvent.id, formData);
      } else {
        // 新增模式
        const eventData = {
          ...formData,
          invitation_id: invitationId
        };
        log.debug('handleSave: 準備儲存事件資料', eventData);
        log.debug('handleSave: invitationId', invitationId);
        await onSave(eventData);
      }
      onClose();
    } catch (error) {
      log.error('保存任務失敗:', error);
      setErrors({ general: '保存失敗，請稍後再試' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!editingEvent || !onDelete) return;

    const confirmDelete = window.confirm('確定要刪除這個任務嗎？此操作無法復原。');
    if (!confirmDelete) return;

    setIsSaving(true);
    try {
      await onDelete(editingEvent.id);
      onClose();
    } catch (error) {
      log.error('刪除任務失敗:', error);
      setErrors({ general: '刪除失敗，請稍後再試' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDateSelect = (date: Date | undefined) => {
    if (date) {
      // 使用本地日期格式化，避免時區轉換問題
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const localDateString = `${year}-${month}-${day}`;
      
      setFormData({
        ...formData,
        target_date: localDateString
      });
      setIsCalendarOpen(false);
    }
  };

  const formatDisplayDate = (dateString: string) => {
    if (!dateString) return '選擇日期';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('zh-TW', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        weekday: 'short'
      });
    } catch {
      return '選擇日期';
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md mx-auto">
        <DialogHeader>
          <DialogTitle className="text-wedding-text-title">
            {editingEvent ? '編輯任務' : '新增任務'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* 任務標題 */}
          <div className="space-y-2">
            <Label htmlFor="title" className="text-wedding-text-body">
              任務標題 *
            </Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="輸入任務標題..."
              className={errors.title ? 'border-red-500' : ''}
            />
            {errors.title && (
              <p className="text-sm text-red-500">{errors.title}</p>
            )}
          </div>

          {/* 目標日期 */}
          <div className="space-y-2">
            <Label className="text-wedding-text-body">目標日期 *</Label>
            <Popover open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
              <PopoverTrigger asChild>
                <button
                  className={cn(
                    "w-full flex items-center justify-between px-3 py-2 text-left border rounded-md",
                    "hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-wedding-pink",
                    errors.target_date ? 'border-red-500' : 'border-gray-300',
                    !formData.target_date && 'text-gray-500'
                  )}
                >
                  <span>{formatDisplayDate(formData.target_date)}</span>
                  <CalendarIcon className="w-4 h-4" />
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={formData.target_date ? new Date(formData.target_date) : undefined}
                  onSelect={handleDateSelect}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
            {errors.target_date && (
              <p className="text-sm text-red-500">{errors.target_date}</p>
            )}
          </div>

          {/* 任務描述 */}
          <div className="space-y-2">
            <Label htmlFor="description" className="text-wedding-text-body">
              任務描述
            </Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="詳細描述任務內容..."
              rows={3}
            />
          </div>

          {/* 分類 */}
          <div className="space-y-2">
            <Label htmlFor="category" className="text-wedding-text-body">
              分類
            </Label>
            <Input
              id="category"
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              placeholder="例如：場地、攝影、邀請函..."
            />
          </div>

          {/* 完成狀態（僅編輯模式顯示） */}
          {editingEvent && (
            <div className="flex items-center space-x-2">
              <Checkbox
                id="completed"
                checked={formData.is_completed}
                onCheckedChange={(checked) => 
                  setFormData({ ...formData, is_completed: Boolean(checked) })
                }
              />
              <Label htmlFor="completed" className="text-wedding-text-body">
                標記為已完成
              </Label>
            </div>
          )}

          {/* 錯誤訊息 */}
          {errors.general && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-600">{errors.general}</p>
            </div>
          )}
        </div>

        <DialogFooter className="flex gap-2">
          {/* 刪除按鈕（僅編輯模式顯示） */}
          {editingEvent && onDelete && (
            <button
              onClick={handleDelete}
              disabled={isSaving}
              className="flex items-center gap-2 px-4 py-2 text-red-600 border border-red-200 rounded-md hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Trash2 className="w-4 h-4" />
              刪除
            </button>
          )}

          {/* 取消和保存按鈕 */}
          <div className="flex gap-2 ml-auto">
            <button
              onClick={onClose}
              disabled={isSaving}
              className="px-4 py-2 text-wedding-text-body border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              取消
            </button>
            <PrimaryButton
              onClick={handleSave}
              disabled={isSaving}
              className="min-w-[80px]"
            >
              {isSaving ? '保存中...' : '保存'}
            </PrimaryButton>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TaskEditor;